import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Calendar, Users, Search, Sparkles, Loader2 } from 'lucide-react';
import Navigation from '../components/Navigation';
import { images } from '../data/images';
import { checkAvailability, getRoomTypeMapping } from '../services/availabilityService';
import buildingImage from '../assets/ChatGPT_Image_1_mars_2026,_19_31_41 copy.png';

type RoomType = 'double' | 'triple' | 'quadruple' | 'family';

interface Room {
  id: RoomType;
  name: string;
  capacity: number;
  image: string;
  availableCount: number;
}

export default function AvailabilityPage() {
  const { t } = useTranslation();
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState(2);
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  const [availableRooms, setAvailableRooms] = useState<Room[]>([]);

  const getRoomImage = (roomType: RoomType) => {
    const imageMap = {
      double: images.rooms.standard.doubleGallery[0],
      triple: images.rooms.standard.tripleGallery[0],
      quadruple: images.rooms.standard.quadGallery[0],
      family: images.rooms.standard.familyGallery[0],
    };
    return imageMap[roomType];
  };

  const getRoomName = (roomType: RoomType) => {
    return t(`rooms.types.${roomType}`);
  };


  const handleSearch = async (e: React.FormEvent | null) => {
    if (e) {
      e.preventDefault();
    }
    setLoading(true);
    setError(null);
    if (e) {
      setShowResults(false);
    }

    try {
      const response = await checkAvailability(checkIn, checkOut, guests);

      const roomsByType = new Map<RoomType, number>();

      response.available_rooms.forEach((room) => {
        const mappedType = getRoomTypeMapping(room.room_type) as RoomType;
        const currentCount = roomsByType.get(mappedType) || 0;
        roomsByType.set(mappedType, currentCount + 1);
      });

      const availableRoomsList: Room[] = [];
      const roomTypes: RoomType[] = ['double', 'triple', 'quadruple', 'family'];
      const capacityMap = { double: 2, triple: 3, quadruple: 4, family: 5 };

      roomTypes.forEach((roomType) => {
        const count = roomsByType.get(roomType) || 0;
        if (count > 0) {
          availableRoomsList.push({
            id: roomType,
            name: getRoomName(roomType),
            capacity: capacityMap[roomType],
            image: getRoomImage(roomType),
            availableCount: count,
          });
        }
      });

      setAvailableRooms(availableRoomsList);
      setShowResults(true);

      if (e) {
        setTimeout(() => {
          resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    } catch (err) {
      setError(t('availability.error'));
      console.error('Availability check error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredRooms = availableRooms;

  const calculateNights = () => {
    if (!checkIn || !checkOut) return 0;
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  };

  const nights = calculateNights();

  return (
    <div className="min-h-screen bg-neutral-50">
      <Navigation />

      <div className="pt-20">
        <div className="relative min-h-[90vh] flex items-center justify-center bg-gradient-to-br from-neutral-900 via-neutral-800 to-stone-900">
          <img
            src={buildingImage}
            alt="Il Gallo Guest House"
            className="absolute inset-0 w-full h-full object-cover opacity-20"
          />

          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full mb-6">
                <Calendar className="w-4 h-4 text-amber-400" />
                <span className="text-white/90 text-sm font-light tracking-wide">{t('availability.checkAvailability')}</span>
              </div>

              <h1 className="text-5xl md:text-6xl lg:text-7xl font-light text-white mb-6 tracking-tight leading-[1.1]">
                {t('availability.findYour')}
                <br />
                <span className="font-serif italic text-neutral-300">{t('availability.availableRoom')}</span>
              </h1>

              <p className="text-neutral-300 text-lg md:text-xl font-light max-w-2xl mx-auto leading-relaxed">
                {t('availability.subtitle')}
              </p>
            </div>

            <form onSubmit={handleSearch} className="max-w-5xl mx-auto">
              <div className="bg-white/98 backdrop-blur-xl rounded-3xl shadow-2xl border border-neutral-200/50 overflow-hidden">
                <div className="p-8 md:p-10">
                  <div className="grid md:grid-cols-7 gap-6 items-end">
                    <div className="md:col-span-2">
                      <label className="block text-xs font-semibold text-neutral-600 uppercase tracking-widest mb-3 pl-1">
                        {t('availability.checkIn')}
                      </label>
                      <div className="relative">
                        <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5 pointer-events-none" />
                        <input
                          type="date"
                          value={checkIn}
                          onChange={(e) => setCheckIn(e.target.value)}
                          className="w-full pl-12 pr-4 py-4 border border-neutral-300 rounded-2xl focus:ring-2 focus:ring-neutral-900 focus:border-neutral-900 transition-all text-base bg-white hover:border-neutral-400"
                          required
                        />
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-xs font-semibold text-neutral-600 uppercase tracking-widest mb-3 pl-1">
                        {t('availability.checkOut')}
                      </label>
                      <div className="relative">
                        <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5 pointer-events-none" />
                        <input
                          type="date"
                          value={checkOut}
                          onChange={(e) => setCheckOut(e.target.value)}
                          className="w-full pl-12 pr-4 py-4 border border-neutral-300 rounded-2xl focus:ring-2 focus:ring-neutral-900 focus:border-neutral-900 transition-all text-base bg-white hover:border-neutral-400"
                          required
                        />
                      </div>
                    </div>

                    <div className="md:col-span-1">
                      <label className="block text-xs font-semibold text-neutral-600 uppercase tracking-widest mb-3 pl-1">
                        {t('availability.guests')}
                      </label>
                      <div className="relative">
                        <Users className="absolute left-4 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5 pointer-events-none" />
                        <select
                          value={guests}
                          onChange={(e) => setGuests(Number(e.target.value))}
                          className="w-full pl-12 pr-3 py-4 border border-neutral-300 rounded-2xl focus:ring-2 focus:ring-neutral-900 focus:border-neutral-900 appearance-none bg-white hover:border-neutral-400 transition-all text-base cursor-pointer"
                        >
                          {[1, 2, 3, 4, 5].map(num => (
                            <option key={num} value={num}>{num}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-neutral-900 hover:bg-neutral-800 disabled:bg-neutral-400 disabled:cursor-not-allowed text-white px-8 py-4 rounded-2xl font-medium transition-all duration-300 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl text-base group"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span>{t('availability.checking')}</span>
                          </>
                        ) : (
                          <>
                            <Search className="w-5 h-5 group-hover:scale-110 transition-transform" />
                            <span>{t('availability.search')}</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>

        <div ref={resultsRef} className="py-20 px-4 sm:px-6 lg:px-8 bg-neutral-50">
          <div className="max-w-7xl mx-auto">
            {error && (
              <div className="mb-8 bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
                <p className="text-red-800 font-medium">{error}</p>
              </div>
            )}

            {showResults && checkIn && checkOut ? (
              <>
                <div className="mb-16">
                  <div className="text-center mb-4">
                    <h2 className="text-4xl md:text-5xl font-light text-neutral-900 mb-3 tracking-tight">
                      {t('availability.availableAccommodations')}
                    </h2>
                    <div className="h-1 w-20 bg-amber-500 mx-auto rounded-full"></div>
                  </div>

                  <div className="flex flex-wrap items-center justify-center gap-6 mt-8">
                    <div className="flex items-center gap-3 px-6 py-3 bg-white rounded-full shadow-sm border border-neutral-200">
                      <Calendar className="w-5 h-5 text-neutral-600" />
                      <span className="text-neutral-700 font-medium">
                        {new Date(checkIn).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(checkOut).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 px-6 py-3 bg-white rounded-full shadow-sm border border-neutral-200">
                      <Users className="w-5 h-5 text-neutral-600" />
                      <span className="text-neutral-700 font-medium">
                        {guests} {guests === 1 ? t('availability.guest') : t('availability.guests')}
                      </span>
                    </div>
                    {nights > 0 && (
                      <div className="flex items-center gap-2 px-6 py-3 bg-white rounded-full shadow-sm border border-neutral-200">
                        <Sparkles className="w-5 h-5 text-amber-500" />
                        <span className="text-neutral-700 font-medium">
                          {nights} {nights === 1 ? t('availability.night') : t('availability.nights')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {filteredRooms.length > 0 ? (
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {filteredRooms.map((room) => (
                      <div
                        key={room.id}
                        className="group bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-neutral-200"
                      >
                        <div className="relative h-64 overflow-hidden">
                          <img
                            src={room.image}
                            alt={room.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                          <div className="absolute top-4 right-4">
                            <div className="bg-emerald-500 text-white px-3 py-1.5 rounded-full text-xs font-medium shadow-lg">
                              {room.availableCount} {room.availableCount === 1 ? t('availability.roomAvailable') : t('availability.roomsAvailable')}
                            </div>
                          </div>
                          <div className="absolute bottom-4 left-4 right-4">
                            <h3 className="text-xl font-medium text-white">
                              {room.name}
                            </h3>
                            <p className="text-white/90 text-sm flex items-center gap-1 mt-1">
                              <Users className="w-4 h-4" />
                              {t('availability.upTo')} {room.capacity} {room.capacity === 1 ? t('availability.guest') : t('availability.guests')}
                            </p>
                          </div>
                        </div>
                        <div className="p-4">
                          <Link
                            to={`/room/${room.id}`}
                            className="block w-full bg-neutral-900 hover:bg-neutral-800 text-white px-4 py-3 rounded-xl font-medium transition-all duration-200 text-center text-sm"
                          >
                            {t('availability.viewDetails')}
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-3xl shadow-md p-16 text-center border border-neutral-200">
                    <div className="w-24 h-24 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Search className="w-12 h-12 text-neutral-400" />
                    </div>
                    <h3 className="text-2xl font-light text-neutral-900 mb-3">
                      {t('availability.noRoomsAvailable')}
                    </h3>
                    <p className="text-neutral-600 max-w-md mx-auto leading-relaxed">
                      {t('availability.noRoomsMessage', { guests })}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white rounded-3xl shadow-md p-16 text-center border border-neutral-200">
                <div className="w-24 h-24 bg-gradient-to-br from-amber-100 to-amber-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Calendar className="w-12 h-12 text-amber-600" />
                </div>
                <h3 className="text-2xl font-light text-neutral-900 mb-3">
                  {t('availability.beginYourSearch')}
                </h3>
                <p className="text-neutral-600 max-w-md mx-auto leading-relaxed">
                  {t('availability.beginSearchMessage')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
